import { createForm } from "@felte/solid";
import { validator } from "@felte/validator-zod";
import { useMutation } from "@tanstack/solid-query";
import { Component } from "solid-js";
import * as zod from "zod";

import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import { CreateDbsCheckPositionMutationVariables } from "../../schema/graphql.js";
import { graphql } from "../../schema/index.js";
import { getGqlErrorMessage } from "../../utils/getGqlErrorMessage.js";
import { Button } from "../common/Button.jsx";
import FormRow from "../common/FormRow.jsx";
import { Checkbox } from "../form/Checkbox.jsx";
import { FormContextProvider } from "../form/FormContext.jsx";
import Input from "../form/Input.jsx";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../modal/Modal.jsx";
import { useToast } from "../toast/index.jsx";

const createDbsCheckPositionMutation = graphql(`
  mutation createDbsCheckPosition(
    $name: String!
    $externalId: Int!
    $workingWithChildrenEntitlement: Boolean!
    $workingWithVulnerableAdultsEntitlement: Boolean!
    $requestAdultFirst: Boolean!
    $workingAtHome: Boolean!
  ) {
    createDbsCheckPosition(
      name: $name
      externalId: $externalId
      workingWithChildrenEntitlement: $workingWithChildrenEntitlement
      workingWithVulnerableAdultsEntitlement: $workingWithVulnerableAdultsEntitlement
      requestAdultFirst: $requestAdultFirst
      workingAtHome: $workingAtHome
    ) {
      id
      name
      externalId
      workingWithChildrenEntitlement
      workingWithVulnerableAdultsEntitlement
      requestAdultFirst
      workingAtHome
    }
  }
`);

const schema = zod.object({
  name: zod.string().min(1, "Name is required"),
  externalId: zod.number().min(1, "External ID is required"),
  workingWithChildrenEntitlement: zod.boolean(),
  workingWithVulnerableAdultsEntitlement: zod.boolean(),
  requestAdultFirst: zod.boolean(),
  workingAtHome: zod.boolean(),
});

export const DbsPositionCreateModal: Component<{
  show: boolean;
  onClose: () => void;
}> = (props) => {
  const toast = useToast();

  const formMethods = createForm({
    initialValues: {
      name: "",
      externalId: null as never, // TS infers a nullable type for externalId which is not correct
      workingWithChildrenEntitlement: false,
      workingWithVulnerableAdultsEntitlement: false,
      requestAdultFirst: false,
      workingAtHome: false,
    },
    extend: validator({ schema }),
    onSubmit: (values) => {
      mutation.mutate(values);
    },
  });

  const mutation = useMutation(() => ({
    mutationFn: async (variables: CreateDbsCheckPositionMutationVariables) => {
      const result = await gqlClient.request(
        createDbsCheckPositionMutation,
        variables,
      );
      return result.createDbsCheckPosition;
    },
    onSuccess: () => {
      toast.success("Success", "DBS check position created");
      props.onClose();
    },
    onError: (error: unknown) => {
      toast.error("Error", getGqlErrorMessage(error));
    },
  }));

  return (
    <Modal show={props.show} onClose={props.onClose}>
      <FormContextProvider formMethods={formMethods}>
        <form onSubmit={formMethods.handleSubmit}>
          <ModalHeader>Create DBS check position</ModalHeader>
          <ModalBody>
            <FormRow label="Name" containerClass="border-none !px-0 !py-1.5">
              <Input name="name" placeholder="Enter position name" />
            </FormRow>
            <FormRow
              label="External ID"
              containerClass="border-none !px-0 !py-1.5"
            >
              <Input
                name="externalId"
                type="number"
                placeholder="Enter external ID"
              />
            </FormRow>
            <FormRow
              label="Working with Children"
              containerClass="border-none !px-0 !py-1.5"
            >
              <Checkbox name="workingWithChildrenEntitlement" />
            </FormRow>
            <FormRow
              label="Working with Vulnerable Adults"
              containerClass="border-none !px-0 !py-1.5"
            >
              <Checkbox name="workingWithVulnerableAdultsEntitlement" />
            </FormRow>
            <FormRow
              label="Adult First Requested"
              containerClass="border-none !px-0 !py-1.5"
            >
              <Checkbox name="requestAdultFirst" />
            </FormRow>
            <FormRow
              label="Working at Home"
              containerClass="border-none !px-0 !py-1.5"
            >
              <Checkbox name="workingAtHome" />
            </FormRow>
          </ModalBody>
          <ModalFooter class="justify-between">
            <Button
              class="w-full"
              type="button"
              color="secondary"
              onClick={props.onClose}
            >
              Cancel
            </Button>
            <Button
              class="w-full"
              type="submit"
              color="primary"
              disabled={!formMethods.isValid() || mutation.isPending}
            >
              Create
            </Button>
          </ModalFooter>
        </form>
      </FormContextProvider>
    </Modal>
  );
};
